import { cloneElement, isValidElement, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./SiteTip.css";

type TipProps = {
  "aria-describedby"?: string;
};

/**
 * Hover and focus tooltip in the site brand color.
 * Renders in a portal so a scrolling or overflow parent does not clip it.
 */
export default function SiteTip({ label, children }: { label: string; children: ReactNode }) {
  const tipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ top: 0, left: 0, below: false });

  const place = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = rect.top < 48;
    setBox({
      top: below ? rect.bottom : rect.top,
      left: rect.left + rect.width / 2,
      below,
    });
  };

  const show = () => {
    place();
    setOpen(true);
  };

  const hide = () => setOpen(false);

  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<TipProps>, {
        "aria-describedby": open ? tipId : (children.props as TipProps)["aria-describedby"],
      })
    : children;

  return (
    <span
      ref={anchorRef}
      className="site-tip-anchor"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {child}
      {open
        ? createPortal(
            <span
              id={tipId}
              role="tooltip"
              className={`site-tip${box.below ? " site-tip--below" : ""}`}
              style={{ top: box.top, left: box.left }}
            >
              {label}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
